import { AfterViewInit, Component, ElementRef, HostListener, OnInit, ViewChild } from "@angular/core";
import { titleSplit } from "src/app/utils/title-split";

@Component({
  selector: "app-playlist",
  templateUrl: "./playlist.component.html",
  styleUrls: ["./playlist.component.scss"],
})
export class PlaylistComponent implements OnInit, AfterViewInit {
  @ViewChild('videoPlayer') videoPlayer!: ElementRef<HTMLVideoElement>;

  playlist: any[] = [];
  genre: string = "";
  data: any;
  flag: string | undefined;

  categorizedLevels: { [key: string]: any[] } = {};

  visible = false;
  active = 0;
  isMobile = false;

  availableQualities: string[] = ['Auto', '480p', '720p', '1080p', '1920p (Full HD)'];
  selectedQuality: string = 'Auto';
  showQualityMenu: boolean = false;

  @HostListener('document:click', ['$event'])
  onDocumentClick(): void {
    this.showQualityMenu = false;
    if (this.videoPlayer?.nativeElement) {
      const video = this.videoPlayer.nativeElement;
      if (video.muted && !video.paused) {
        video.muted = false;
      }
    }
  }

  toggleQualityMenu(event: Event): void {
    event.stopPropagation();
    this.showQualityMenu = !this.showQualityMenu;
  }

  getQualityClass(): string {
    switch (this.selectedQuality) {
      case '480p':
        return 'quality-480p';
      case '720p':
        return 'quality-720p';
      case '1080p':
        return 'quality-1080p';
      case '1920p (Full HD)':
        return 'quality-1920p';
      default:
        return 'quality-Auto';
    }
  }

  changeQuality(quality: string): void {
    if (this.selectedQuality === quality) {
      this.showQualityMenu = false;
      return;
    }

    const player = this.videoPlayer?.nativeElement;
    const currentTime = player ? player.currentTime : 0;
    const isPaused = player ? player.paused : true;

    this.selectedQuality = quality;
    this.showQualityMenu = false;

    if (player) {
      const targetSrc = this.getVideoSource();

      if (player.src !== targetSrc) {
        player.pause();

        const onLoadedMetadata = () => {
          player.removeEventListener('loadedmetadata', onLoadedMetadata);
          try {
            if (currentTime > 0 && !isNaN(player.duration) && currentTime < player.duration) {
              player.currentTime = currentTime;
            }
          } catch (err) {
            console.warn('Unable to seek video to currentTime:', err);
          }
          if (!isPaused) {
            player.play().catch(err => console.warn('Quality change playback error:', err));
          }
        };

        const onError = () => {
          player.removeEventListener('error', onError);
          player.removeEventListener('loadedmetadata', onLoadedMetadata);
          console.warn('Resolution variant URL not found on server, applying quality mode to base stream.');
          const fallbackSrc = this.getVideoSource(true);
          if (player.src !== fallbackSrc) {
            player.src = fallbackSrc;
            player.load();
            if (!isPaused) {
              player.play().catch(e => console.warn('Fallback play error:', e));
            }
          }
        };

        player.addEventListener('loadedmetadata', onLoadedMetadata);
        player.addEventListener('error', onError, { once: true });

        player.src = targetSrc;
        player.load();
      }
    }
  }

  getVideoSource(forceBase: boolean = false): string {
    if (!this.data) return '';
    const baseVideo = this.data.video || '';

    if (forceBase || this.selectedQuality === 'Auto' || !baseVideo) {
      return baseVideo;
    }

    if (this.data.qualities && this.data.qualities[this.selectedQuality]) {
      return this.data.qualities[this.selectedQuality];
    }

    const qualityKeyMap: { [key: string]: string[] } = {
      '480p': ['video_480p', 'video_480', 'url_480p'],
      '720p': ['video_720p', 'video_720', 'url_720p'],
      '1080p': ['video_1080p', 'video_1080', 'url_1080p'],
      '1920p (Full HD)': ['video_1920p', 'video_1920', 'video_1080p', 'url_1920p']
    };

    const keys = qualityKeyMap[this.selectedQuality] || [];
    for (const key of keys) {
      if (this.data[key]) {
        return this.data[key];
      }
    }

    const suffixMap: { [key: string]: string } = {
      '480p': '_480p',
      '720p': '_720p',
      '1080p': '_1080p',
      '1920p (Full HD)': '_1920p'
    };

    const suffix = suffixMap[this.selectedQuality];
    if (suffix && baseVideo.includes('.mp4') && !baseVideo.includes('X-Amz-Signature') && !baseVideo.includes('Signature=')) {
      const parts = baseVideo.split('.mp4');
      return `${parts[0]}${suffix}.mp4${parts.slice(1).join('.mp4')}`;
    }

    return baseVideo;
  }

  ngOnInit(): void {
    this.isMobile = window.innerWidth <= 768;

    this.playlist = history.state.playlist || [];
    this.genre = history.state.name || "";

    this.categorizePlaylist();
    const firstLevelKey = Object.keys(this.categorizedLevels)[0];
    if (firstLevelKey) {
      this.flag = firstLevelKey;
      this.data = this.categorizedLevels[firstLevelKey][0];
      this.active = 0;
    }
  }

  ngAfterViewInit(): void {
    if (this.videoPlayer?.nativeElement) {
      const video = this.videoPlayer.nativeElement;
      video.muted = false;
      video.load();
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(err => {
          console.warn('First video autoplay blocked by browser, attempting muted play:', err);
          video.muted = true;
          video.play().catch(e => console.warn('Muted play error:', e));
        });
      }
    }
  }

  categorizePlaylist() {
    for (const item of this.playlist) {
      const levelKey = item.level || "Uncategorized";
      if (!this.categorizedLevels[levelKey]) {
        this.categorizedLevels[levelKey] = [];
      }
      this.categorizedLevels[levelKey].push(item);
    }
  }

  setVideo(index: number, flag: string) {
    this.flag = flag;
    this.data = this.categorizedLevels[flag][index];
    this.active = index;

    if (this.videoPlayer?.nativeElement) {
      const video = this.videoPlayer.nativeElement;
      video.muted = false;
      video.load();
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(err => {
          console.warn('Unmuted autoplay restricted by browser, playing muted until user gesture:', err);
          video.muted = true;
          video.play().catch(e => console.warn('Muted play error:', e));
        });
      }
    }

    if (this.isMobile) {
      this.setVisibility();
    }
  }

  setVisibility() {
    this.visible = !this.visible;
  }

  skipBackward(seconds: number = 10): void {
    if (this.videoPlayer && this.videoPlayer.nativeElement) {
      const player = this.videoPlayer.nativeElement;
      player.currentTime = Math.max(0, player.currentTime - seconds);
    }
  }

  skipForward(seconds: number = 10): void {
    if (this.videoPlayer && this.videoPlayer.nativeElement) {
      const player = this.videoPlayer.nativeElement;
      const targetTime = player.currentTime + seconds;
      player.currentTime = isNaN(player.duration) ? targetTime : Math.min(player.duration, targetTime);
    }
  }

  hasNextVideo(): boolean {
    if (!this.flag || !this.categorizedLevels[this.flag]) return false;
    const levelKeys = Object.keys(this.categorizedLevels);
    const currentLevelIdx = levelKeys.indexOf(this.flag);
    const currentLevelVideos = this.categorizedLevels[this.flag];

    if (this.active + 1 < currentLevelVideos.length) {
      return true;
    }
    return currentLevelIdx + 1 < levelKeys.length;
  }

  playNextVideo(): void {
    if (!this.flag || !this.categorizedLevels[this.flag]) return;
    const levelKeys = Object.keys(this.categorizedLevels);
    const currentLevelIdx = levelKeys.indexOf(this.flag);
    const currentLevelVideos = this.categorizedLevels[this.flag];

    if (this.active + 1 < currentLevelVideos.length) {
      this.setVideo(this.active + 1, this.flag);
      this.autoplayVideo();
    } else if (currentLevelIdx + 1 < levelKeys.length) {
      const nextLevelKey = levelKeys[currentLevelIdx + 1];
      this.setVideo(0, nextLevelKey);
      this.autoplayVideo();
    }
  }

  private autoplayVideo(): void {
    setTimeout(() => {
      if (this.videoPlayer && this.videoPlayer.nativeElement) {
        this.videoPlayer.nativeElement.play().catch(err => {
          console.warn('Autoplay prevented or interrupted:', err);
        });
      }
    }, 150);
  }
}

